import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { components } from '@/api/generated/schema';
import { isApiError } from '@/api/errors';
import { Banner, Button, DiffView, Field, Modal, TextArea, type DiffField } from '@/components/ui';
import { useCursorPager, type CursorPage } from '@/components/data';
import './settings.css';

export type Schemas = components['schemas'];

/** Paged org list query driven by the shared cursor pager. `fetchPage` receives cursor, limit and abort signal. */
export function usePagedList<T>(
  key: unknown[],
  fetchPage: (q: { cursor?: string; limit: number }, signal: AbortSignal) => Promise<CursorPage<T>>,
  opts?: { enabled?: boolean },
) {
  const pager = useCursorPager(JSON.stringify(key));
  const query = useQuery({
    queryKey: [...key, { cursor: pager.cursor, limit: pager.pageSize }],
    queryFn: ({ signal }) => fetchPage({ cursor: pager.cursor, limit: pager.pageSize }, signal),
    placeholderData: (prev) => prev,
    enabled: opts?.enabled,
  });
  return { pager, query };
}

/** Field errors from a 422 problem, keyed by JSON path (last segment also mapped). */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!isApiError(error)) return {};
  const out: Record<string, string> = {};
  for (const e of error.problem.errors ?? []) {
    if (!e.path) continue;
    const msg = e.message ?? e.code ?? 'Invalid value';
    out[e.path] = msg;
    const last = e.path.split(/[./]/).filter(Boolean).pop();
    if (last) out[last] ??= msg;
  }
  return out;
}

export function FormErrorSummary({ error }: { error: unknown }) {
  if (!isApiError(error) || error.notImplemented) return null;
  const errs = error.problem.errors ?? [];
  if (error.versionConflict) {
    return (
      <Banner tone="accent" role="alert" title="Newer version saved">
        Your edits are kept. Reload the current values, compare, then save again.
      </Banner>
    );
  }
  if (!errs.length) return null;
  return (
    <Banner tone="accent" role="alert" title="Invalid fields">
      <ul>
        {errs.map((e, i) => (
          <li key={i}>
            <span className="mono">{e.path ?? 'request'}</span>: {e.message ?? e.code}
          </li>
        ))}
      </ul>
    </Banner>
  );
}

/** Shown on every settings page for non admins. The server enforces this regardless. */
export function ReadOnlyNotice({ canEdit }: { canEdit: boolean }) {
  if (canEdit) return null;
  return (
    <Banner title="Read only">
      Showing effective values. Editing requires an organization admin.
    </Banner>
  );
}

/**
 * Confirmation listing exact effects, with an optional required reason.
 * Never a generic "Are you sure".
 */
export function ConfirmDialog({
  open,
  onClose,
  title,
  effects,
  confirmLabel,
  onConfirm,
  pending,
  danger,
  reason = 'required',
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  effects: ReactNode[];
  confirmLabel: string;
  onConfirm: (reason: string) => void;
  pending?: boolean;
  danger?: boolean;
  reason?: 'required' | 'none';
  children?: ReactNode;
}) {
  const [text, setText] = useState('');
  const disabled = reason === 'required' && !text.trim();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={pending}
            disabled={disabled}
            onClick={() => onConfirm(text.trim())}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div>
          <p className="small muted">What happens</p>
          <ul className="stack-sm">
            {effects.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
        {children}
        {reason === 'required' && (
          <Field label="Reason" required hint="Recorded in the audit log.">
            <TextArea rows={2} value={text} maxLength={500} onChange={(e) => setText(e.target.value)} />
          </Field>
        )}
      </div>
    </Modal>
  );
}

/** Save/Cancel bar with a diff review step and a required reason. */
export function SaveBar({
  fields,
  effect,
  onSave,
  onCancel,
  pending,
  reason = 'required',
}: {
  fields: DiffField[];
  effect?: ReactNode;
  onSave: (reason: string) => void;
  onCancel: () => void;
  pending?: boolean;
  reason?: 'required' | 'none';
}) {
  const [review, setReview] = useState(false);
  const [text, setText] = useState('');
  const changed = fields.filter((f) => JSON.stringify(f.before) !== JSON.stringify(f.after));
  return (
    <>
      <div className="row">
        <Button variant="primary" disabled={!changed.length} onClick={() => setReview(true)}>
          Review and save
        </Button>
        <Button variant="secondary" disabled={!changed.length} onClick={onCancel}>
          Cancel
        </Button>
        <span className="small muted">{changed.length ? `${changed.length} unsaved change${changed.length === 1 ? '' : 's'}` : 'No changes'}</span>
      </div>
      <Modal
        open={review}
        onClose={() => setReview(false)}
        title="Review changes"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setReview(false)}>
              Back to editing
            </Button>
            <Button
              variant="primary"
              loading={pending}
              disabled={reason === 'required' && !text.trim()}
              onClick={() => {
                onSave(text.trim());
                setReview(false);
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="stack">
          <DiffView fields={changed} />
          {effect && <Banner>{effect}</Banner>}
          {reason === 'required' && (
            <Field label="Reason" required hint="Saved with the new settings.">
              <TextArea rows={2} value={text} maxLength={500} onChange={(e) => setText(e.target.value)} />
            </Field>
          )}
        </div>
      </Modal>
    </>
  );
}

export function scopeLabel(scope: string): string {
  return scope.replace(':', ' ');
}

/** Scopes in one table cell: humanized, cut with an ellipsis, the full list on hover. */
export function ScopeList({ scopes }: { scopes: readonly string[] }) {
  if (scopes.length === 0) return <span className="small faint">None</span>;
  const text = scopes.map(scopeLabel).join(', ');
  return (
    <span className="small settings-cell-list" title={text}>
      {text}
    </span>
  );
}

export function boolText(v: boolean | null | undefined): string {
  return v == null ? 'Unknown' : v ? 'On' : 'Off';
}

export function secondsText(s: number | null | undefined): string {
  if (s == null) return 'Unknown';
  if (s === 0) return 'Immediately';
  if (s % 3600 === 0) return `${s / 3600} hour${s === 3600 ? '' : 's'}`;
  if (s % 60 === 0) return `${s / 60} minutes`;
  return `${s} seconds`;
}
